import React, { DetailedHTMLProps, forwardRef, InputHTMLAttributes, ReactNode } from "react";
import { useFormContext } from "react-hook-form";

import { FormField } from "../form-field/form-field";
import { FormFieldContainer } from "../form-field/form-field-container";
import { FormFieldLabel } from "../form-field/form-field-label";
import { FormFieldMessage } from "../form-field/form-field-message";
import {
  FormFieldColors,
  FormFieldLabelProps,
  FormFieldMessageProps,
  FormFieldStatus,
} from "../form-field/types";

export interface InputProps
  extends DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>,
    FormFieldLabelProps,
    FormFieldMessageProps {
  color?: FormFieldColors;
  startDecoration?: ReactNode;
  endDecoration?: ReactNode;
  optionalLabel?: string;
  collapseMessage?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      color = FormFieldColors.Light,
      message,
      status = FormFieldStatus.Info,
      required,
      placeholder,
      label,
      info,
      className,
      labelDecoration,
      startDecoration,
      endDecoration,
      optionalLabel,
      collapseMessage = true,
      ...rest
    },
    ref
  ) => {
    const { disabled } = rest;

    const formMethods = useFormContext();
    const hasValue = !!rest.value || (rest?.name && formMethods?.watch(rest.name)) || false;

    return (
      <FormFieldContainer disabled={disabled} className={className}>
        {label && (
          <FormFieldLabel
            required={required}
            label={label}
            info={info}
            labelDecoration={labelDecoration}
            optionalLabel={optionalLabel}
          />
        )}
        <FormField color={color} hasValue={hasValue} className="h-11">
          {startDecoration && <div className="pl-2">{startDecoration}</div>}
          <input
            ref={ref}
            className="px-3 py-1"
            required={required}
            disabled={disabled}
            placeholder={placeholder}
            {...rest}
          />
          {endDecoration && <div className="pr-2">{endDecoration}</div>}
        </FormField>

        {(message || !collapseMessage) && <FormFieldMessage message={message} status={status} />}
      </FormFieldContainer>
    );
  }
);
